import React from 'react';
import {
	RefreshControl,
	ScrollView,
	TouchableOpacity,
	View,
} from 'react-native';

import {
	MY_DAY_STEP_STATUS,
	isDocketDoneForMyDay,
} from '../../../common/utils/myDay/driverDaySteps';
import { getLocalTime } from '../../../common/utils/time/time';
import {
	Button,
	ErrorCallout,
	Heading,
	Label,
	Spinner,
	Text,
} from '../../components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import PhaseSection from './board/PhaseSection';
import StepFocusCard from './board/StepFocusCard';
import StepRow from './board/StepRow';
import StopsList from './board/StopsList';
import useEmployeeDayPreview from './useEmployeeDayPreview';

const EmployeeMyDayPreviewView = ({
	employee,
	onBack,
	onChooseEmployee,
	canEngage,
	onEngage,
}) => {
	const styles = useThemedStyles(createStyles);
	const {
		isOnline,
		timezone,
		loading,
		error,
		refreshPreview,
		isRefreshing,
		detailedRuns,
		activeRun,
		activeRunId,
		setSelectedRunId,
		setSelectedDocketId,
		sortedDockets,
		activeDocket,
		startSteps,
		endSteps,
		perStopSteps,
		runStarted,
		nextActionCopy,
		showStopsSection,
		stopsAsSteps,
		employeeName,
		dateLabel,
		dataWarning,
	} = useEmployeeDayPreview(employee);

	return (
		<ScrollView
			style={styles.container}
			scrollIndicatorInsets={{ right: 1 }}
			refreshControl={(
				<RefreshControl
					refreshing={isRefreshing}
					onRefresh={refreshPreview}
				/>
			)}
		>
			<View style={styles.content}>
				<View style={styles.readOnlyNotice}>
					<View style={styles.noticeTitleRow}>
						<Label label="Preview · read-only" secondary={true} style={styles.noticeLabel} />
						<Text style={styles.noticeEmployee} numberOfLines={2}>{employeeName}</Text>
					</View>
					<Text style={styles.noticeText}>
						{'This is the employee\'s recorded My Day. Run, clock, pick, return and completion actions are unavailable.'}
					</Text>
				</View>

				<View style={styles.headerCard}>
					<Heading heading={`${employeeName}'s My day`} subHeading={dateLabel} />
					<View style={styles.headerActions}>
						<Button label="Back to my day" onPress={onBack} style={styles.headerActionButton} />
						<Button label="Choose employee" onPress={onChooseEmployee} style={styles.headerActionButton} />
					</View>
				</View>

				{Boolean(dataWarning) && <ErrorCallout text={dataWarning} />}
				{
					detailedRuns.length > 1 && (
						<View style={styles.runChipsRow}>
							{
								detailedRuns.map((run, index) => {
									const runId = String(run.id);
									return (
										<TouchableOpacity
											key={runId}
											style={[styles.runChip, runId === activeRunId && styles.runChipActive]}
											activeOpacity={0.86}
											onPress={() => {
												setSelectedRunId(runId);
												setSelectedDocketId(null);
											}}
										>
											<Text style={[styles.runChipText, runId === activeRunId && styles.runChipTextActive]}>
												{`${index + 1}. ${run.route?.name || 'Routeless run'} · ${getLocalTime(timezone, run.planned).format('HH:mm')}`}
											</Text>
										</TouchableOpacity>
									);
								})
							}
						</View>
					)
				}

				{
					loading && !activeRun
						? <Spinner />
						: !activeRun
							? (
								<View style={styles.emptyCard}>
									<Heading heading={error ? 'Employee day unavailable' : 'No runs today'} />
									<Text style={styles.subtleText}>
										{error
											? `${employeeName}'s runs could not be loaded. Try again when the connection is available.`
											: `No runs were found for ${employeeName} today.`}
									</Text>
								</View>
							)
							: (
								<>
									{Boolean(canEngage) && (
										<Button
											label={isOnline ? 'Work this run for training' : 'Connect to work this run'}
											onPress={() => onEngage?.(activeRun.id)}
											disabled={!isOnline}
											style={styles.engageButton}
										/>
									)}
									<StepFocusCard
										kicker="Next in My Day · read-only"
										title={nextActionCopy.title}
										description={nextActionCopy.description}
									/>
									<PhaseSection title="Start of day" steps={startSteps}>
										{
											startSteps.map(step => <StepRow key={step.key} step={step} />)
										}
										{
											!startSteps.some(step => step.status !== MY_DAY_STEP_STATUS.HIDDEN) && (
												<Text style={styles.subtleText}>Nothing to prepare for this run.</Text>
											)
										}
									</PhaseSection>
									<PhaseSection title="On the run" steps={stopsAsSteps}>
										{
											!sortedDockets.length
												? <Text style={styles.subtleText}>No stops were found for this run.</Text>
												: !showStopsSection
													? <Text style={styles.subtleText}>The stop list appears after required start-of-day preparation is complete.</Text>
													: (
														<StopsList
															sortedDockets={sortedDockets}
															currentDocketId={activeDocket?.id}
															isDocketDone={isDocketDoneForMyDay}
															onSelectStop={docket => setSelectedDocketId(docket.id)}
														/>
													)
										}
									</PhaseSection>
									{
										showStopsSection && activeDocket && (
											<PhaseSection
												title={isDocketDoneForMyDay(activeDocket) ? 'Selected completed stop' : 'Selected stop'}
												steps={perStopSteps}
											>
												{perStopSteps.map(step => <StepRow key={step.key} step={step} />)}
											</PhaseSection>
										)
									}
									{
										runStarted
											? (
												<PhaseSection title="End of day" steps={endSteps}>
													{endSteps.map(step => <StepRow key={step.key} step={step} />)}
												</PhaseSection>
											)
											: (
												<View style={styles.emptyCard}>
													<Text style={styles.emptyTitle}>End of day</Text>
													<Text style={styles.subtleText}>This section appears after the employee starts the service run.</Text>
												</View>
											)
									}
								</>
							)
				}
			</View>
		</ScrollView>
	);
};

const createStyles = (theme) => ({
	container: {
		backgroundColor: theme.mainBg4,
		flex: 1,
	},
	content: {
		padding: 10,
		paddingBottom: 30,
	},
	readOnlyNotice: {
		backgroundColor: theme.mainBg3,
		borderWidth: 1,
		borderColor: theme.border3,
		borderRadius: 10,
		padding: 12,
		marginBottom: 10,
	},
	noticeTitleRow: {
		flexDirection: 'row',
		alignItems: 'center',
		flexWrap: 'wrap',
	},
	noticeLabel: {
		marginBottom: 0,
		marginRight: 6,
	},
	noticeEmployee: {
		color: theme.text1,
		fontWeight: '700',
		flexShrink: 1,
	},
	noticeText: {
		color: theme.text2,
		fontSize: 13,
		marginTop: 6,
	},
	headerCard: {
		backgroundColor: theme.mainBg1,
		borderRadius: 12,
		padding: 12,
		marginBottom: 10,
		borderWidth: 1,
		borderColor: theme.border3,
	},
	headerActions: {
		flexDirection: 'row',
		marginHorizontal: -4,
	},
	headerActionButton: {
		flex: 1,
		marginHorizontal: 4,
	},
	engageButton: {
		marginBottom: 10,
	},
	runChipsRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		marginBottom: 4,
	},
	runChip: {
		backgroundColor: theme.mainBg1,
		borderRadius: 16,
		paddingVertical: 6,
		paddingHorizontal: 12,
		marginRight: 6,
		marginBottom: 6,
		borderWidth: 1,
		borderColor: theme.border3,
	},
	runChipActive: {
		backgroundColor: theme.mainBg3,
		borderColor: theme.primary1,
	},
	runChipText: {
		fontSize: 13,
		color: theme.text2,
	},
	runChipTextActive: {
		color: theme.text1,
		fontWeight: '600',
	},
	emptyCard: {
		backgroundColor: theme.mainBg1,
		borderRadius: 12,
		padding: 12,
		marginBottom: 10,
		borderWidth: 1,
		borderColor: theme.border3,
	},
	emptyTitle: {
		fontSize: 16,
		fontWeight: '700',
		color: theme.text1,
		marginBottom: 4,
	},
	subtleText: {
		color: theme.text2,
	},
});

export default EmployeeMyDayPreviewView;
